import type { ReactNode } from 'react';
import { Stepper, Card, CardBody } from '@vnetwork/ui/primitives';
import { JourneyStepRow } from './JourneyStepRow';
import { stepList } from './onboarding-styles';

export interface JourneyStep {
  title: string;
  description: string;
  icon?: ReactNode;
  /** CTA shown only when this is the current step. */
  actionLabel?: string;
  onAction?: () => void;
}

export interface JourneyTimelineProps {
  steps: JourneyStep[];
  /** Steps before this index render done; this one current; after, upcoming. */
  currentIndex: number;
}

/**
 * The shared onboarding journey: a horizontal `Stepper` progress header over a
 * vertical, state-aware timeline of step rows wrapped in a `Card`. Reused by the
 * no-project Get Started guide (B) and the no-monitor first-run checklist (C) so
 * the two read as one consistent flow.
 */
export function JourneyTimeline({ steps, currentIndex }: JourneyTimelineProps) {
  const stepFor = (i: number) => (i < currentIndex ? 'done' : i === currentIndex ? 'current' : 'upcoming');

  return (
    <Card>
      <CardBody style={{ display: 'grid', gap: 'var(--vn-space-5)' }}>
        <Stepper
          aria-label="Setup progress"
          steps={steps.map((s) => ({ label: s.title }))}
          current={Math.min(currentIndex, steps.length - 1)}
          reached={currentIndex}
        />

        <div style={stepList}>
          {steps.map((s, i) => (
            <JourneyStepRow
              key={s.title}
              index={i + 1}
              title={s.title}
              description={s.description}
              icon={s.icon}
              state={stepFor(i)}
              actionLabel={s.actionLabel}
              onAction={s.onAction}
            />
          ))}
        </div>
      </CardBody>
    </Card>
  );
}
